import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Download,
  LogOut,
  Loader2,
  Globe,
  CheckCircle2,
  Clock,
  Landmark,
  Mail,
  MessageCircle,
  Users,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gd_admin_token";

const DIVISIONES = [
  { key: "transporte", name: "Transporte Disimil", accent: "#38BDF8" },
  { key: "logistica", name: "Logística Deportiva", accent: "#10B981" },
];

const money = (v, currency) =>
  `${currency === "USD" ? "$" : "Bs"} ${Number(v || 0).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const divisionOf = (key) => DIVISIONES.find((d) => d.key === key) || DIVISIONES[0];

const StatusBadge = ({ status }) =>
  status === "pagada" ? (
    <span className="inline-flex items-center gap-1.5 border border-emerald-800/60 bg-emerald-950/50 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-400">
      <CheckCircle2 className="h-3 w-3" /> Pagada
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 border border-amber-800/60 bg-amber-950/50 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-400">
      <Clock className="h-3 w-3" /> Pendiente
    </span>
  );

const inputCls =
  "w-full border border-line bg-void px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 focus:border-gold/50 focus:outline-none";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400";

function NewInvoice({ headers, onCreated, onCancel, prefill }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    division: prefill?.divisions?.[0] || "transporte",
    client_name: prefill?.name || "",
    client_rif: prefill?.rif || "",
    client_email: prefill?.email || "",
    client_phone: prefill?.phone || "",
    international: !!prefill?.international,
    currency: "USD",
    notes: "",
  });
  const [items, setItems] = useState([{ description: "", qty: 1, price: "" }]);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    axios
      .get(`${API}/admin/service-catalog?division=${form.division}`, { headers })
      .then(({ data }) => setCatalog(data.items || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.division]);

  const searchClients = async (q) => {
    if (q.trim().length < 2) {
      setSuggestions([]);
      setShowSug(false);
      return;
    }
    try {
      const { data } = await axios.get(
        `${API}/admin/clients?q=${encodeURIComponent(q)}`,
        { headers }
      );
      setSuggestions(data);
      setShowSug(data.length > 0);
    } catch {
      setSuggestions([]);
    }
  };

  const pickClient = (c) => {
    setForm((s) => ({
      ...s,
      client_name: c.name,
      client_rif: c.rif || "",
      client_email: c.email || "",
      client_phone: c.phone || "",
      international: !!c.international,
    }));
    setShowSug(false);
    setSuggestions([]);
    if (c.name) toast.success(`${c.number} — ${c.name}: datos cargados`);
  };

  const applyCatalog = (i, desc) => {
    const found = catalog.find((c) => c.description === desc);
    setItems((s) =>
      s.map((it, idx) =>
        idx === i
          ? { ...it, description: desc, price: found ? String(found.price) : it.price }
          : it
      )
    );
  };
  const setItem = (i, k, v) =>
    setItems((s) => s.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));

  const subtotal = items.reduce(
    (acc, it) => acc + (Number(it.qty) || 0) * (Number(it.price) || 0),
    0
  );
  const iva = form.international ? 0 : subtotal * 0.16;
  const total = subtotal + iva;

  const save = async () => {
    if (!form.client_name.trim() || !form.client_rif.trim()) {
      toast.error("Completa el nombre y el RIF del cliente");
      return;
    }
    const validItems = items.filter(
      (it) => it.description.trim() && Number(it.qty) > 0 && Number(it.price) > 0
    );
    if (validItems.length === 0) {
      toast.error("Agrega al menos un ítem con descripción y precio");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/invoices`,
        {
          ...form,
          client_email: form.client_email.trim() || null,
          client_phone: form.client_phone.trim() || null,
          notes: form.notes.trim() || null,
          items: validItems.map((it) => ({
            description: it.description.trim(),
            qty: Number(it.qty),
            price: Number(it.price),
          })),
        },
        { headers }
      );
      toast.success(`Factura ${data.number} creada`);
      onCreated(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin/login");
      } else {
        toast.error("No se pudo crear la factura");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="invoice-form">
      <p className={labelCls}>División que factura</p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {DIVISIONES.map((d) => (
          <button
            key={d.key}
            type="button"
            onClick={() => set("division", d.key)}
            data-testid={`form-division-${d.key}`}
            className={`border px-4 py-3.5 text-sm font-medium transition-colors ${
              form.division === d.key
                ? "text-void"
                : "border-line bg-void text-slate-300 hover:border-slate-500"
            }`}
            style={form.division === d.key ? { backgroundColor: d.accent, borderColor: d.accent } : {}}
          >
            {d.name}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-4">
        <div className="relative">
          <p className={labelCls}>Cliente *</p>
          <input
            className={`${inputCls} mt-2`}
            placeholder="Escribe el nombre — si ya existe, salen sus datos"
            value={form.client_name}
            onChange={(e) => {
              set("client_name", e.target.value);
              searchClients(e.target.value);
            }}
            onFocus={() => suggestions.length > 0 && setShowSug(true)}
            onBlur={() => setTimeout(() => setShowSug(false), 250)}
            data-testid="input-client-name"
          />
          {showSug && suggestions.length > 0 && (
            <div
              className="absolute z-20 mt-1 w-full border border-line bg-ink shadow-2xl"
              data-testid="client-suggestions"
            >
              {suggestions.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => pickClient(c)}
                  data-testid={`client-suggestion-${c.number}`}
                  className="flex w-full items-center justify-between gap-3 border-b border-line/60 px-4 py-3 text-left last:border-b-0 hover:bg-void"
                >
                  <span>
                    <span className="block text-sm text-slate-200">{c.name}</span>
                    <span className="font-mono text-[10px] text-slate-500">{c.rif}</span>
                  </span>
                  <span className="flex flex-col items-end gap-1">
                    <span className="font-mono text-[10px] tracking-[0.16em] text-gold">
                      {c.number}
                    </span>
                    <span
                      className={`font-mono text-[9px] uppercase tracking-[0.14em] ${
                        c.type === "internacional" ? "text-sky-400" : "text-emerald-400"
                      }`}
                    >
                      {c.type === "internacional" ? "Internacional" : "Nacional"}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <p className={labelCls}>RIF *</p>
            <input
              className={`${inputCls} mt-2`}
              placeholder="J-00000000-0"
              value={form.client_rif}
              onChange={(e) => set("client_rif", e.target.value)}
              data-testid="input-client-rif"
            />
          </div>
          <div>
            <p className={labelCls}>Teléfono</p>
            <input
              className={`${inputCls} mt-2`}
              placeholder="+58 ..."
              value={form.client_phone}
              onChange={(e) => set("client_phone", e.target.value)}
              data-testid="input-client-phone"
            />
          </div>
        </div>
        <div>
          <p className={labelCls}>Correo del cliente</p>
          <input
            type="email"
            className={`${inputCls} mt-2`}
            placeholder="cliente@correo.com"
            value={form.client_email}
            onChange={(e) => set("client_email", e.target.value)}
            data-testid="input-client-email"
          />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => set("international", !form.international)}
          data-testid="toggle-international"
          className={`flex items-center gap-3 border px-4 py-3.5 text-left text-sm transition-colors ${
            form.international
              ? "border-gold/60 bg-gold/10 text-gold"
              : "border-line bg-void text-slate-400"
          }`}
        >
          <Globe className="h-4 w-4 shrink-0" />
          <span>
            Cliente internacional
            <span className="block text-xs opacity-70">Sin IVA (exento)</span>
          </span>
        </button>
        <div>
          <p className={labelCls}>Moneda</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {["USD", "VES"].map((cur) => (
              <button
                key={cur}
                type="button"
                onClick={() => set("currency", cur)}
                data-testid={`currency-${cur}`}
                className={`border px-4 py-3 font-mono text-sm transition-colors ${
                  form.currency === cur
                    ? "border-gold bg-gold text-void"
                    : "border-line bg-void text-slate-300"
                }`}
              >
                {cur === "USD" ? "$ USD" : "Bs VES"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <p className={labelCls}>Ítems del servicio *</p>
          <button
            type="button"
            onClick={() => setItems((s) => [...s, { description: "", qty: 1, price: "" }])}
            data-testid="btn-add-item"
            className="inline-flex items-center gap-1.5 border border-gold/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-gold hover:bg-gold hover:text-void"
          >
            <Plus className="h-3 w-3" /> Ítem
          </button>
        </div>
        <div className="mt-3 space-y-3">
          {items.map((it, i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_auto] gap-2 border border-line bg-void/50 p-3 sm:grid-cols-[1fr_90px_130px_auto] sm:items-center"
            >
              <select
                className="col-span-2 border border-line bg-void px-3 py-2.5 text-xs text-slate-400 focus:border-gold/50 focus:outline-none sm:col-span-4"
                value={
                  catalog.some((c) => c.description === it.description) ? it.description : ""
                }
                onChange={(e) => e.target.value && applyCatalog(i, e.target.value)}
                data-testid={`item-catalog-${i}`}
              >
                <option value="">Elegir servicio del catálogo…</option>
                {catalog.map((c) => (
                  <option key={c.description} value={c.description}>
                    {c.description} ({money(c.price, form.currency)})
                  </option>
                ))}
              </select>
              <input
                className={inputCls}
                placeholder="Descripción del servicio"
                value={it.description}
                onChange={(e) => setItem(i, "description", e.target.value)}
                data-testid={`item-desc-${i}`}
              />
              <input
                type="number"
                min="1"
                className={inputCls}
                placeholder="Cant."
                value={it.qty}
                onChange={(e) => setItem(i, "qty", e.target.value)}
                data-testid={`item-qty-${i}`}
              />
              <input
                type="number"
                min="0"
                step="0.01"
                className={inputCls}
                placeholder="Precio"
                value={it.price}
                onChange={(e) => setItem(i, "price", e.target.value)}
                data-testid={`item-price-${i}`}
              />
              <button
                type="button"
                onClick={() => setItems((s) => s.filter((_, idx) => idx !== i))}
                disabled={items.length === 1}
                data-testid={`item-remove-${i}`}
                className="flex h-11 w-11 items-center justify-center border border-line text-slate-500 transition-colors hover:border-red-800 hover:text-red-400 disabled:opacity-30"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <p className={labelCls}>Notas</p>
        <textarea
          rows={2}
          className={`${inputCls} mt-2 resize-none`}
          placeholder="Condiciones, referencias, etc."
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          data-testid="input-notes"
        />
      </div>

      <div className="mt-6 space-y-1.5 border-t border-line pt-5 text-right">
        <p className="text-sm text-slate-400">
          Subtotal: <span className="text-slate-200">{money(subtotal, form.currency)}</span>
        </p>
        <p className="text-sm text-slate-400">
          IVA (16%):{" "}
          <span className="text-slate-200">
            {form.international ? "Exento" : money(iva, form.currency)}
          </span>
        </p>
        <p className="font-serif text-2xl font-semibold text-gold" data-testid="form-total">
          Total: {money(total, form.currency)}
        </p>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="btn-save-invoice"
          className="flex flex-1 items-center justify-center gap-2 bg-gold px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-void hover:opacity-90 disabled:opacity-60"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Emitir factura
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="border border-line px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-slate-400 hover:border-slate-500 hover:text-slate-200"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

function InvoiceDetail({ inv, headers, onBack, onChanged }) {
  const navigate = useNavigate();
  const d = divisionOf(inv.division);
  const [downloading, setDownloading] = useState(false);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(`${API}/admin/invoices/${inv.id}/pdf`, {
        headers,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `factura-${inv.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin/login");
      } else toast.error("No se pudo descargar el PDF");
    } finally {
      setDownloading(false);
    }
  };

  const toggleStatus = async () => {
    const next = inv.status === "pagada" ? "pendiente" : "pagada";
    try {
      const { data } = await axios.patch(
        `${API}/admin/invoices/${inv.id}/status`,
        { status: next },
        { headers }
      );
      toast.success(next === "pagada" ? "Factura marcada como pagada" : "Factura marcada como pendiente");
      onChanged(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin/login");
      } else toast.error("No se pudo actualizar el estado");
    }
  };

  const [payment, setPayment] = useState(null);
  const [sendingMail, setSendingMail] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/payment-info`, { headers })
      .then(({ data }) => setPayment(data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendEmail = async () => {
    if (!inv.client_email) {
      toast.error("Esta factura no tiene correo del cliente");
      return;
    }
    setSendingMail(true);
    try {
      await axios.post(`${API}/admin/invoices/${inv.id}/email`, {}, { headers });
      toast.success(`Factura enviada a ${inv.client_email}`);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin/login");
      } else {
        toast.error(e.response?.data?.detail || "No se pudo enviar el correo");
      }
    } finally {
      setSendingMail(false);
    }
  };

  const openWhatsApp = () => {
    const lines = [
      `Hola ${inv.client_name}, te compartimos la factura ${inv.number} de ${d.name}.`,
      `Total: ${money(inv.total, inv.currency)} (${inv.currency})`,
    ];
    if (payment) {
      const usa = [
        payment.usa_bank,
        payment.usa_zelle ? `Zelle: ${payment.usa_zelle}` : null,
        payment.usa_holder ? `Titular: ${payment.usa_holder}` : null,
      ]
        .filter(Boolean)
        .join(" · ");
      const ve = [
        payment.ve_bank,
        payment.ve_account ? `Cta: ${payment.ve_account}` : null,
        payment.ve_holder,
      ]
        .filter(Boolean)
        .join(" · ");
      if (usa || ve || payment.ve_pago_movil) {
        lines.push("", "Dónde pagar:");
        if (usa) lines.push(`USA — ${usa}`);
        if (ve) lines.push(`Venezuela — ${ve}`);
        if (payment.ve_pago_movil) lines.push(`Pago móvil — ${payment.ve_pago_movil}`);
      }
    }
    lines.push("", "Grupo Disimil — Excelencia en cada detalle");
    const phone = (inv.client_phone || "").replace(/\D/g, "");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  return (
    <div data-testid="invoice-detail">
      <button
        onClick={onBack}
        className="mb-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-gold"
      >
        <ArrowLeft className="h-4 w-4" /> Volver a la lista
      </button>

      <div className="border border-line bg-ink p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: d.accent }}>
              {d.name}
            </p>
            <h2 className="mt-2 font-serif text-3xl font-semibold text-slate-100">
              Factura {inv.number}
            </h2>
            <p className="mt-1 text-xs text-slate-500">{inv.created_at?.slice(0, 10)}</p>
          </div>
          <StatusBadge status={inv.status} />
        </div>

        <div className="mt-6 border-t border-line pt-5">
          <p className={labelCls}>Cliente</p>
          <p className="mt-2 text-base font-medium text-slate-100">{inv.client_name}</p>
          {inv.client_number && (
            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-gold">
              {inv.client_number} · {inv.client_type === "internacional" ? "Internacional" : "Nacional"}
            </p>
          )}
          <p className="text-sm text-slate-400">RIF: {inv.client_rif}</p>
          {inv.client_phone && <p className="text-sm text-slate-400">Tel: {inv.client_phone}</p>}
          {inv.client_email && <p className="text-sm text-slate-400">{inv.client_email}</p>}
          {inv.international && (
            <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-gold">
              <Globe className="h-3 w-3" /> Cliente internacional — exento de IVA
            </p>
          )}
        </div>

        <div className="mt-6 space-y-2">
          {inv.items.map((it, i) => (
            <div
              key={i}
              className="flex items-baseline justify-between gap-4 border-b border-line/60 pb-2 text-sm"
            >
              <span className="text-slate-300">
                {it.description}
                <span className="ml-2 text-xs text-slate-500">
                  x{it.qty} @ {money(it.price, inv.currency)}
                </span>
              </span>
              <span className="whitespace-nowrap text-slate-100">
                {money(it.qty * it.price, inv.currency)}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-5 space-y-1 text-right text-sm text-slate-400">
          <p>
            Subtotal: <span className="text-slate-200">{money(inv.subtotal, inv.currency)}</span>
          </p>
          <p>
            IVA (16%): <span className="text-slate-200">{money(inv.iva, inv.currency)}</span>
          </p>
          <p className="font-serif text-2xl font-semibold text-gold">
            Total: {money(inv.total, inv.currency)}
          </p>
        </div>

        {inv.notes && (
          <p className="mt-4 border-t border-line pt-4 text-sm font-light text-slate-400">
            {inv.notes}
          </p>
        )}

        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <button
            onClick={downloadPdf}
            disabled={downloading}
            data-testid="btn-download-pdf"
            className="flex items-center justify-center gap-2 bg-gold px-4 py-4 font-mono text-[10px] uppercase tracking-[0.18em] text-void hover:opacity-90 disabled:opacity-60"
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            PDF
          </button>
          <button
            onClick={openWhatsApp}
            data-testid="btn-send-whatsapp"
            className="flex items-center justify-center gap-2 border border-emerald-700/60 bg-emerald-950/30 px-4 py-4 font-mono text-[10px] uppercase tracking-[0.18em] text-emerald-400 transition-colors hover:bg-emerald-950/60"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </button>
          <button
            onClick={sendEmail}
            disabled={sendingMail}
            data-testid="btn-send-email"
            className="flex items-center justify-center gap-2 border border-sky-700/60 bg-sky-950/30 px-4 py-4 font-mono text-[10px] uppercase tracking-[0.18em] text-sky-400 transition-colors hover:bg-sky-950/60 disabled:opacity-60"
          >
            {sendingMail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Email
          </button>
          <button
            onClick={toggleStatus}
            data-testid="btn-toggle-status"
            className={`border px-4 py-4 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
              inv.status === "pagada"
                ? "border-amber-800/60 text-amber-400 hover:bg-amber-950/40"
                : "border-slate-600 text-slate-300 hover:bg-slate-800/40"
            }`}
          >
            {inv.status === "pagada" ? "Marcar pendiente" : "Marcar pagada"}
          </button>
        </div>
        {!inv.client_email && (
          <p className="mt-3 text-center text-xs text-slate-500">
            Sin correo del cliente: el botón Email necesita el correo en la factura. WhatsApp usa el
            teléfono del cliente si está cargado.
          </p>
        )}
      </div>
    </div>
  );
}

function PaymentSettings({ headers, onBack }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    usa_bank: "",
    usa_zelle: "",
    usa_holder: "",
    ve_bank: "",
    ve_account: "",
    ve_holder: "",
    ve_pago_movil: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    axios
      .get(`${API}/admin/payment-info`, { headers })
      .then(({ data }) =>
        setForm((s) => ({
          ...s,
          ...Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v || ""])),
        }))
      )
      .catch((e) => {
        if (e.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          navigate("/admin/login");
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/payment-info`,
        Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim() || null])),
        { headers }
      );
      toast.success("Datos de pago guardados. Aparecerán en las facturas PDF.");
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin/login");
      } else {
        toast.error("No se pudieron guardar los datos");
      }
    } finally {
      setSaving(false);
    }
  };

  const groups = [
    {
      title: "Cuenta en USA",
      fields: [
        { k: "usa_bank", label: "Banco", ph: "Ej: Bank of America", testid: "pay-usa-bank" },
        { k: "usa_zelle", label: "Zelle (correo o teléfono)", ph: "Ej: pagos@disimil.com", testid: "pay-usa-zelle" },
        { k: "usa_holder", label: "Titular", ph: "Nombre del titular", testid: "pay-usa-holder" },
      ],
    },
    {
      title: "Cuenta en Venezuela",
      fields: [
        { k: "ve_bank", label: "Banco", ph: "Ej: Banesco", testid: "pay-ve-bank" },
        { k: "ve_account", label: "Número de cuenta", ph: "0134-0000-00-0000000000", testid: "pay-ve-account" },
        { k: "ve_holder", label: "Titular y RIF", ph: "Grupo Disimil C.A. — J-00000000-0", testid: "pay-ve-holder" },
        { k: "ve_pago_movil", label: "Pago móvil (teléfono · banco · RIF)", ph: "0412-0000000 · Banesco · J-...", testid: "pay-ve-pago-movil" },
      ],
    },
  ];

  return (
    <div data-testid="payment-settings">
      <button
        onClick={onBack}
        className="mb-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-gold"
      >
        <ArrowLeft className="h-4 w-4" /> Volver a la lista
      </button>

      <h2 className="font-serif text-xl font-semibold text-slate-100">Datos de pago</h2>
      <p className="mt-2 text-sm font-light text-slate-400">
        Estas cuentas aparecerán en la sección "Métodos de pago" de cada factura PDF que emitas,
        para ambas divisiones.
      </p>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gold" />
        </div>
      ) : (
        <>
          {groups.map((g) => (
            <div key={g.title} className="mt-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-gold">
                {g.title}
              </p>
              <div className="mt-3 space-y-3">
                {g.fields.map((f) => (
                  <div key={f.k}>
                    <p className={labelCls}>{f.label}</p>
                    <input
                      className={`${inputCls} mt-1.5`}
                      placeholder={f.ph}
                      value={form[f.k]}
                      onChange={(e) => set(f.k, e.target.value)}
                      data-testid={f.testid}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <button
            onClick={save}
            disabled={saving}
            data-testid="btn-save-payment-info"
            className="mt-8 flex w-full items-center justify-center gap-2 bg-gold px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-void hover:opacity-90 disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Guardar datos de pago
          </button>
        </>
      )}
    </div>
  );
}

function ClientsView({ headers, onBack, onInvoiceFor }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async (query = "") => {
    try {
      const { data } = await axios.get(
        `${API}/admin/clients${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ""}`,
        { headers }
      );
      setClients(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin/login");
      } else {
        toast.error("No se pudieron cargar los clientes");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="clients-view">
      <button
        onClick={onBack}
        className="mb-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-gold"
      >
        <ArrowLeft className="h-4 w-4" /> Volver a la lista
      </button>

      <h2 className="font-serif text-xl font-semibold text-slate-100">Base de clientes</h2>
      <p className="mt-2 text-sm font-light text-slate-400">
        Cada cliente facturado queda registrado con su número (CLI-0001…) y tipo Nacional o
        Internacional. Al facturar de nuevo, escribe el nombre y se cargan sus datos.
      </p>

      <input
        className={`${inputCls} mt-5`}
        placeholder="Buscar cliente por nombre…"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          load(e.target.value);
        }}
        data-testid="clients-search"
      />

      <div className="mt-5 space-y-3" data-testid="clients-list">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gold" />
          </div>
        ) : clients.length === 0 ? (
          <div className="border border-dashed border-line py-16 text-center">
            <p className="text-sm text-slate-500">
              Aún no hay clientes. Se registran automáticamente al emitir facturas.
            </p>
          </div>
        ) : (
          clients.map((c) => (
            <div
              key={c.id}
              data-testid={`client-card-${c.number}`}
              className="flex flex-wrap items-center justify-between gap-3 border border-line bg-void/60 px-5 py-4"
            >
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-mono text-xs tracking-[0.16em] text-gold">{c.number}</span>
                  <span
                    className={`border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] ${
                      c.type === "internacional"
                        ? "border-sky-800/60 text-sky-400"
                        : "border-emerald-800/60 text-emerald-400"
                    }`}
                  >
                    {c.type === "internacional" ? "Internacional" : "Nacional"}
                  </span>
                </div>
                <p className="mt-1.5 text-sm font-medium text-slate-100">{c.name}</p>
                <p className="text-xs text-slate-500">
                  RIF: {c.rif}
                  {c.phone ? ` · ${c.phone}` : ""}
                  {c.email ? ` · ${c.email}` : ""}
                </p>
              </div>
              <button
                onClick={() => onInvoiceFor(c)}
                data-testid={`btn-invoice-for-${c.number}`}
                className="inline-flex items-center gap-2 border border-gold/40 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-gold transition-colors hover:bg-gold hover:text-void"
              >
                <Plus className="h-3.5 w-3.5" /> Facturar
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function AdminFacturas() {
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [view, setView] = useState("list");
  const [invoices, setInvoices] = useState([]);
  const [current, setCurrent] = useState(null);
  const [divisionFilter, setDivisionFilter] = useState("todas");
  const [statusFilter, setStatusFilter] = useState("todas");
  const [loading, setLoading] = useState(true);
  const [prefillClient, setPrefillClient] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/invoices`, { headers });
      setInvoices(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin/login");
      } else {
        toast.error("No se pudieron cargar las facturas");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = invoices.filter(
    (inv) =>
      (divisionFilter === "todas" || inv.division === divisionFilter) &&
      (statusFilter === "todas" || inv.status === statusFilter)
  );

  const totals = useMemo(() => {
    const t = { pendiente: { USD: 0, VES: 0 }, pagada: { USD: 0, VES: 0 } };
    invoices.forEach((inv) => {
      if (t[inv.status] && t[inv.status][inv.currency] !== undefined) {
        t[inv.status][inv.currency] += inv.total;
      }
    });
    return t;
  }, [invoices]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    navigate("/admin/login");
  };

  return (
    <main className="min-h-screen bg-void px-4 py-8 sm:px-6" data-testid="admin-dashboard">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-[10px] font-bold tracking-[0.35em] text-slate-100">
                GRUPO
              </span>
              <span className="text-gold-gradient font-serif text-xl font-bold">Disimil</span>
            </div>
            <h1 className="mt-2 font-serif text-2xl font-semibold text-slate-100 sm:text-3xl">
              Facturación
            </h1>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 border border-line px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:border-slate-500 hover:text-slate-200"
          >
            <LogOut className="h-3.5 w-3.5" /> Salir
          </button>
        </div>

        {view === "list" && (
          <>
            <div className="mt-8 grid grid-cols-2 gap-3">
              <div className="border border-amber-800/40 bg-ink p-5" data-testid="summary-pending">
                <p className={labelCls}>Por cobrar</p>
                <p className="mt-2 font-serif text-xl font-semibold text-amber-400 sm:text-2xl">
                  {money(totals.pendiente.USD, "USD")}
                </p>
                <p className="text-sm text-amber-400/70">{money(totals.pendiente.VES, "VES")}</p>
              </div>
              <div className="border border-emerald-800/40 bg-ink p-5" data-testid="summary-paid">
                <p className={labelCls}>Cobrado</p>
                <p className="mt-2 font-serif text-xl font-semibold text-emerald-400 sm:text-2xl">
                  {money(totals.pagada.USD, "USD")}
                </p>
                <p className="text-sm text-emerald-400/70">{money(totals.pagada.VES, "VES")}</p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {["todas", ...DIVISIONES.map((d) => d.key)].map((key) => (
                <button
                  key={key}
                  onClick={() => setDivisionFilter(key)}
                  data-testid={`filter-division-${key}`}
                  className={`border px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors ${
                    divisionFilter === key
                      ? "border-gold bg-gold text-void"
                      : "border-line text-slate-400 hover:border-slate-500"
                  }`}
                >
                  {key === "todas" ? "Todas" : divisionOf(key).name}
                </button>
              ))}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="filter-status"
                className="border border-line bg-void px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-300 focus:outline-none"
              >
                <option value="todas">Todos los estados</option>
                <option value="pendiente">Pendientes</option>
                <option value="pagada">Pagadas</option>
              </select>
              <button
                onClick={() => setView("new")}
                data-testid="btn-nueva-factura"
                className="ml-auto inline-flex items-center gap-2 bg-gold px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-void hover:opacity-90"
              >
                <Plus className="h-3.5 w-3.5" /> Nueva factura
              </button>
            </div>

            <div className="mt-6 space-y-3" data-testid="invoice-list">
              {loading ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-gold" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="border border-dashed border-line py-16 text-center">
                  <p className="text-sm text-slate-500">
                    No hay facturas con estos filtros. Emite la primera con "Nueva factura".
                  </p>
                </div>
              ) : (
                filtered.map((inv) => {
                  const d = divisionOf(inv.division);
                  return (
                    <button
                      key={inv.id}
                      onClick={() => {
                        setCurrent(inv);
                        setView("detail");
                      }}
                      data-testid={`invoice-row-${inv.number}`}
                      className="flex w-full flex-wrap items-center justify-between gap-3 border border-line bg-ink px-5 py-4 text-left transition-colors hover:border-slate-600"
                    >
                      <div className="flex items-center gap-4">
                        <span
                          className="h-9 w-1.5 shrink-0"
                          style={{ backgroundColor: d.accent }}
                        />
                        <div>
                          <p className="font-mono text-sm font-medium text-slate-100">
                            {inv.number}
                          </p>
                          <p className="text-sm font-light text-slate-400">{inv.client_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-medium text-slate-100">
                            {money(inv.total, inv.currency)}
                          </p>
                          <p className="text-xs text-slate-500">{inv.created_at?.slice(0, 10)}</p>
                        </div>
                        <StatusBadge status={inv.status} />
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}

        {view === "new" && (
          <div className="mt-8 border border-line bg-ink p-6 sm:p-8">
            <h2 className="mb-6 font-serif text-xl font-semibold text-slate-100">
              Nueva factura
            </h2>
            <NewInvoice
              headers={headers}
              prefill={prefillClient}
              onCancel={() => {
                setPrefillClient(null);
                setView("list");
              }}
              onCreated={(inv) => {
                setPrefillClient(null);
                setCurrent(inv);
                setView("detail");
                load();
              }}
            />
          </div>
        )}

        {view === "clients" && (
          <div className="mt-8 border border-line bg-ink p-6 sm:p-8">
            <ClientsView
              headers={headers}
              onBack={() => setView("list")}
              onInvoiceFor={(c) => {
                setPrefillClient(c);
                setView("new");
              }}
            />
          </div>
        )}

        {view === "detail" && current && (
          <div className="mt-8">
            <InvoiceDetail
              inv={current}
              headers={headers}
              onBack={() => {
                setView("list");
                load();
              }}
              onChanged={(updated) => {
                setCurrent(updated);
                load();
              }}
            />
          </div>
        )}

        {view === "payments" && (
          <div className="mt-8 border border-line bg-ink p-6 sm:p-8">
            <PaymentSettings headers={headers} onBack={() => setView("list")} />
          </div>
        )}

        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <button
            onClick={() => setView("clients")}
            data-testid="btn-clients"
            className="inline-flex items-center gap-2 border border-sky-800/60 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-sky-400 transition-colors hover:bg-sky-950/40"
          >
            <Users className="h-3.5 w-3.5" /> Base de clientes
          </button>
          <button
            onClick={() => setView("payments")}
            data-testid="btn-payment-info"
            className="inline-flex items-center gap-2 border border-gold/40 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-void"
          >
            <Landmark className="h-3.5 w-3.5" /> Dónde pagar — cuentas USA / Venezuela
          </button>
        </div>
        <div className="mt-4 text-center">
          <Link
            to="/"
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600 transition-colors hover:text-gold"
          >
            Volver al sitio público
          </Link>
        </div>
      </div>
    </main>
  );
}
